import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Collapse
} from '@mui/material';
import {
  QuestionAnswer as QuestionAnswerIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useServiceRequestQueries, formatDate } from 'shared-lib';

interface QASectionProps {
  requestId: string;
}

/**
 * QA Section component for service requests
 * Allows vendors to ask anonymous questions and view answers
 */
export const QASection: React.FC<QASectionProps> = ({ requestId }) => {
  const [newQuestion, setNewQuestion] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get hooks for questions
  const { useQuestionsByRequest, useAskQuestion } = useServiceRequestQueries();
  
  // Fetch questions for this service request
  const { 
    data: questions = [], 
    isLoading: questionsLoading 
  } = useQuestionsByRequest(requestId);
  
  // Mutation for asking a question
  const { 
    mutateAsync: askQuestion, 
    isPending: isSubmitting 
  } = useAskQuestion();

  // Handler for submitting a new question
  const handleSubmitQuestion = async () => {
    if (!newQuestion.trim()) {
      setError('Question cannot be empty');
      return;
    }
    
    try {
      setError(null);
      await askQuestion({ 
        requestId, 
        text: newQuestion 
      });
      setNewQuestion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit question');
    }
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Sort questions by creation date, newest first
  const sortedQuestions = [...questions].sort((a, b) => 
    b.createdAt.getTime() - a.createdAt.getTime()
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3, 
        border: '1px solid rgba(16, 29, 59, 0.08)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{ 
          p: 2, 
          bgcolor: 'rgba(16, 29, 59, 0.02)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(16, 29, 59, 0.08)',
          cursor: 'pointer'
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuestionAnswerIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Q&A Section
          </Typography>
          <Chip 
            label={questions.length} 
            size="small" 
            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            color="primary" 
          />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* Question input field */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 1,
              mb: 3 
            }}
          >
            <TextField
              fullWidth
              placeholder="Ask a question about this service request"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              disabled={isSubmitting}
              multiline
              rows={2}
              inputProps={{
                maxLength: 1000
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmitQuestion}
              disabled={isSubmitting || !newQuestion.trim()}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ 
                mt: { xs: 1, md: 0 },
                height: { md: '100%' },
                whiteSpace: 'nowrap'
              }}
            >
              {isSubmitting ? 'Sending...' : 'Ask Question'}
            </Button>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Questions list */}
          {questionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : sortedQuestions.length > 0 ? (
            <List sx={{ width: '100%' }}>
              {sortedQuestions.map((question, index) => (
                <React.Fragment key={question.id}>
                  <ListItem 
                    alignItems="flex-start" 
                    sx={{ 
                      flexDirection: 'column',
                      py: 2
                    }}
                  >
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500,
                          mb: 0.5
                        }}
                      >
                        Q: {question.text}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                      >
                        Asked {formatDate(question.createdAt)}
                      </Typography>
                    </Box>
                    
                    {question.answer ? (
                      <Box 
                        sx={{ 
                          width: '100%',
                          mt: 1,
                          p: 2,
                          bgcolor: 'rgba(16, 29, 59, 0.03)',
                          borderRadius: '8px',
                          border: '1px solid rgba(16, 29, 59, 0.06)'
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 0.5,
                            fontWeight: 500
                          }}
                        >
                          A: {question.answer}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                        >
                          Answered {formatDate(question.answeredAt)}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          mt: 1,
                          p: 2,
                          bgcolor: 'rgba(255, 152, 0, 0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 152, 0, 0.1)'
                        }}
                      >
                        <Typography variant="body2" color="warning.main">
                          Awaiting answer from client
                        </Typography>
                      </Box>
                    )}
                  </ListItem>
                  {index < sortedQuestions.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No questions have been asked yet. Be the first to ask!
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default QASection; 